import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, X } from 'lucide-react';
import { buildPageList } from '../../utils/gmpBoard';
import { formatDateRange, formatDayShort } from '../../utils/ipoCalendar';
import { PLATFORMS, SME_STATUS } from '../../utils/smeIpos';

// Rows shown per page; the table has no size picker
export const PAGE_SIZE = 10;
const COLUMN_COUNT = 11;

const Dash = () => <span className="text-slate-300">—</span>;

const inr = (value) => `₹${Math.round(value).toLocaleString('en-IN')}`;

const timingNote = (row, today) => {
  if (row.status === 'closing_today') return 'Bidding closes 5:00 PM today';
  if (row.status === 'open') return row.closeOn ? `Closes ${formatDayShort(row.closeOn)}` : 'Bidding open';
  if (row.status === 'upcoming') {
    if (!row.openOn) return 'Dates to be announced';
    return row.openOn === today ? 'Opens today' : `Opens ${formatDayShort(row.openOn)}`;
  }
  if (row.status === 'listed' && row.listingOn) return `Listed ${formatDayShort(row.listingOn)}`;
  return row.closeOn ? `Closed ${formatDayShort(row.closeOn)}` : 'Bidding closed';
};

const GmpCell = ({ row }) => {
  if (row.gmp === null) return <span className="text-[11px] text-slate-400">Awaiting</span>;
  const tone = row.gmp > 0 ? 'text-emerald-600' : row.gmp < 0 ? 'text-rose-600' : 'text-slate-600';
  return (
    <div className={`font-bold ${tone}`}>
      {row.gmp > 0 ? '+' : row.gmp < 0 ? '−' : ''}₹{Math.abs(row.gmp)}
      {typeof row.gmpPercent === 'number' && (
        <span className="ml-1 text-[11px] font-semibold">({row.gmpPercent > 0 ? '+' : ''}{row.gmpPercent.toFixed(1)}%)</span>
      )}
      {row.estListing !== null && <div className="text-[10px] font-medium text-slate-400">Est. listing {inr(row.estListing)}</div>}
    </div>
  );
};

const PageButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

export const SmeIposTable = forwardRef(
  ({ rows, today, page, onPageChange, query, onQueryChange, isLoading, emptyMessage }, ref) => {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const firstIndex = rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const lastIndex = Math.min(currentPage * PAGE_SIZE, rows.length);
    const showing = `Showing ${firstIndex} to ${lastIndex} of ${rows.length} SME ${rows.length === 1 ? 'IPO' : 'IPOs'}`;

    return (
      <div ref={ref} className="scroll-mt-20 space-y-3">
        <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 px-1 text-xs text-slate-500">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{showing}</span>
            <span className="hidden sm:inline text-slate-300" aria-hidden="true">
              |
            </span>
            <span>
              Sorted by: <strong className="font-semibold text-slate-700">Bidding Status &amp; Date</strong>
            </span>
          </p>
          <label className="relative block w-full sm:w-72">
            <span className="sr-only">Search SME IPOs by company or symbol</span>
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search company or symbol..."
              className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-700 placeholder-slate-400 shadow-sm focus:outline-none focus:border-[#008080] focus:ring-2 focus:ring-[#008080]/15"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onQueryChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
        </div>

        <section aria-label="SME IPO list" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="overflow-x-auto scrollbar-slim">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[200px] sm:min-w-[240px]">
                    Company
                  </th>
                  <th scope="col" className="py-3 px-2 whitespace-nowrap">Platform</th>
                  <th scope="col" className="py-3 px-2 whitespace-nowrap">Status</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Bidding Window</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Price Band</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Lot Size</th>
                  <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">Issue Size</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">GMP</th>
                  <th scope="col" className="py-3 px-3 text-right whitespace-nowrap">Subscription</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Allotment / Listing</th>
                  <th scope="col" className="py-3 pl-2 pr-4 sm:pr-5 text-right whitespace-nowrap">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
                {isLoading &&
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`sme-skel-${idx}`} className="animate-pulse">
                      <td className="sticky left-0 z-10 bg-white py-3.5 pl-4 sm:pl-5 pr-3">
                        <div className="w-44 h-4 bg-slate-200 rounded" />
                        <div className="mt-1.5 w-24 h-3 bg-slate-100 rounded" />
                      </td>
                      {Array.from({ length: COLUMN_COUNT - 1 }).map((__, col) => (
                        <td key={col} className="py-3.5 px-3">
                          <div className="w-14 h-4 bg-slate-100 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  pageRows.map((row) => {
                    const live = row.status === 'open' || row.status === 'closing_today';
                    const stickyTint = live ? 'bg-emerald-50' : 'bg-white group-hover:bg-slate-50';
                    return (
                      <tr key={row.id} className={`group align-middle transition-colors ${live ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70'}`}>
                        <td className={`sticky left-0 z-10 py-3.5 pl-4 sm:pl-5 pr-3 ${stickyTint}`}>
                          <Link to={row.detailPath} className="font-bold text-slate-900 hover:text-[#008080] transition-colors">
                            {row.company}
                          </Link>
                          <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                            {live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0" aria-hidden="true" />}
                            {[row.symbol, timingNote(row, today)].filter(Boolean).join(' · ')}
                          </p>
                        </td>
                        <td className="py-3.5 px-2">
                          {row.exchange ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap">
                              {PLATFORMS[row.exchange]}
                            </span>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="py-3.5 px-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold whitespace-nowrap ${SME_STATUS[row.status].badge}`}>
                            {SME_STATUS[row.status].label}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap text-slate-700">
                          {row.openOn && row.closeOn ? formatDateRange(row.openOn, row.closeOn) : <Dash />}
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap font-semibold text-slate-800">{row.priceBand || <Dash />}</td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          {row.lotSize ? (
                            <>
                              <div className="font-semibold text-slate-800">{row.lotSize.toLocaleString('en-IN')} shares</div>
                              {row.lotValue && <div className="text-[10px] text-slate-400">{inr(row.lotValue)} per lot</div>}
                            </>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="py-3.5 px-3 text-right whitespace-nowrap tabular-nums text-slate-700">
                          {row.sizeCr ? `₹${row.sizeCr.toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr` : <Dash />}
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          <GmpCell row={row} />
                        </td>
                        <td className="py-3.5 px-3 text-right whitespace-nowrap tabular-nums">
                          {typeof row.subscription === 'number' ? (
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-extrabold ${
                                row.subscription >= 1
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                  : 'bg-rose-50 text-rose-600 border-rose-200'
                              }`}
                            >
                              {row.subscription.toFixed(2)}x
                            </span>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap text-[11px] leading-tight text-slate-500">
                          {row.allotmentOn || row.listingOn ? (
                            <>
                              <div>Allotment: {row.allotmentOn ? formatDayShort(row.allotmentOn) : '—'}</div>
                              <div>Listing: {row.listingOn ? formatDayShort(row.listingOn) : '—'}</div>
                            </>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="py-3.5 pl-2 pr-4 sm:pr-5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Catalog issues open their full IPO page; the rest their grey-market page */}
                            <Link
                              to={row.detailPath}
                              className="inline-flex items-center px-2.5 py-1.5 rounded-lg bg-[#008080] hover:bg-[#006666] text-[11px] font-bold text-white whitespace-nowrap"
                            >
                              Details
                            </Link>
                            {/* Only when it is somewhere else: for an issue with no
                                catalog record, Details already is the GMP page */}
                            {row.detailPath !== `/ipo-gmp/${row.gmpSlug}` && (
                              <Link
                                to={`/ipo-gmp/${row.gmpSlug}`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-teal-200 hover:text-[#008080] whitespace-nowrap"
                              >
                                GMP
                                <ArrowUpRight className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <p className="text-xs text-slate-500">{isLoading ? 'Loading SME IPOs…' : showing}</p>
            {totalPages > 1 && (
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="SME IPO table pagination">
                <PageButton onClick={() => onPageChange(1)} disabled={currentPage <= 1} label="First page">
                  <ChevronsLeft className="w-3.5 h-3.5" />
                  First
                </PageButton>
                <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} label="Previous page">
                  <ChevronLeft className="w-3.5 h-3.5" />
                  Prev
                </PageButton>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPageChange(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                        p === currentPage ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <PageButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} label="Next page">
                  Next
                  <ChevronRight className="w-3.5 h-3.5" />
                </PageButton>
                <PageButton onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} label="Last page">
                  Last
                  <ChevronsRight className="w-3.5 h-3.5" />
                </PageButton>
              </nav>
            )}
          </div>
        </section>
      </div>
    );
  }
);

SmeIposTable.displayName = 'SmeIposTable';

export default SmeIposTable;
